import type {
  NotificationPreferences,
  ProviderAvailabilityRequest,
  ProviderPortalNotification,
  SignedAction
} from "@kuroo/contracts";
import type { AvailabilityException, AvailabilityRule } from "./client-catalog-types";

export type SdkAvailabilityContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function availabilityRules(context: SdkAvailabilityContext, filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityRule[]> {
  const query = new URLSearchParams();
  if (filters.providerId) query.set("providerId", filters.providerId);
  if (filters.locationId) query.set("locationId", filters.locationId);
  return context.request<AvailabilityRule[]>(`/api/v1/availability/rules${query.size ? `?${query}` : ""}`);
}

export function createAvailabilityRule(context: SdkAvailabilityContext, input: { providerId?: string; serviceId?: string | null; locationId?: string; weekday: number; startTime: string; endTime: string; timezone?: string; validFrom?: string; validUntil?: string }): Promise<AvailabilityRule> {
  return context.request<AvailabilityRule>("/api/v1/availability/rules", { method: "POST", body: JSON.stringify(input) });
}

export function deleteAvailabilityRule(context: SdkAvailabilityContext, ruleId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/availability/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
}

export function availabilityExceptions(context: SdkAvailabilityContext, filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityException[]> {
  const query = new URLSearchParams();
  if (filters.providerId) query.set("providerId", filters.providerId);
  if (filters.locationId) query.set("locationId", filters.locationId);
  return context.request<AvailabilityException[]>(`/api/v1/availability/exceptions${query.size ? `?${query}` : ""}`);
}

export function availabilityRequests(context: SdkAvailabilityContext, filters: { status?: ProviderAvailabilityRequest["status"]; providerId?: string } = {}): Promise<ProviderAvailabilityRequest[]> {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  if (filters.providerId) query.set("providerId", filters.providerId);
  return context.request<ProviderAvailabilityRequest[]>(`/api/v1/availability/requests${query.size ? `?${query}` : ""}`);
}

export function createAvailabilityRequest(context: SdkAvailabilityContext, input: {
  requestType: ProviderAvailabilityRequest["requestType"];
  action?: ProviderAvailabilityRequest["action"];
  targetId?: string;
  effectiveFrom?: string;
  serviceId?: string | null;
  locationId?: string | null;
  current?: Record<string, unknown>;
  requested: Record<string, unknown>;
  reason?: string;
  category?: string;
  urgent?: boolean;
}): Promise<ProviderAvailabilityRequest> {
  return context.request<ProviderAvailabilityRequest>("/api/v1/availability/requests", { method: "POST", body: JSON.stringify(input) });
}

export function withdrawAvailabilityRequest(context: SdkAvailabilityContext, requestId: string): Promise<ProviderAvailabilityRequest> {
  return context.request<ProviderAvailabilityRequest>(`/api/v1/availability/requests/${encodeURIComponent(requestId)}/withdraw`, { method: "POST", body: "{}" });
}

export function reviewAvailabilityRequest(context: SdkAvailabilityContext, requestId: string, decision: "approve" | "reject", comment?: string): Promise<ProviderAvailabilityRequest> {
  return context.request<ProviderAvailabilityRequest>(`/api/v1/availability/requests/${encodeURIComponent(requestId)}/${decision}`, { method: "POST", body: JSON.stringify({ comment }) });
}

export function urgentProviderBlock(context: SdkAvailabilityContext, input: { startsAt: string; endsAt: string; locationId?: string | null; reason?: string; category?: string }): Promise<ProviderAvailabilityRequest> {
  return createAvailabilityRequest(context, { requestType: "block", action: "create", requested: { startsAt: input.startsAt, endsAt: input.endsAt, kind: "blocked" }, locationId: input.locationId, reason: input.reason, category: input.category, urgent: true });
}

export function providerNotifications(context: SdkAvailabilityContext): Promise<ProviderPortalNotification[]> {
  return context.request<ProviderPortalNotification[]>("/api/v1/provider/notifications");
}

export function markProviderNotificationRead(context: SdkAvailabilityContext, notificationId: string): Promise<{ id: string; readAt: string }> {
  return context.request<{ id: string; readAt: string }>(`/api/v1/provider/notifications/${encodeURIComponent(notificationId)}/read`, { method: "POST", body: "{}" });
}

export function providerNotificationPreferences(context: SdkAvailabilityContext): Promise<NotificationPreferences> {
  return context.request<NotificationPreferences>("/api/v1/provider/notification-preferences");
}

export function updateProviderNotificationPreferences(context: SdkAvailabilityContext, input: Partial<Omit<NotificationPreferences, "userId" | "updatedAt">>): Promise<NotificationPreferences> {
  return context.request<NotificationPreferences>("/api/v1/provider/notification-preferences", { method: "PATCH", body: JSON.stringify(input) });
}

export function createAvailabilityException(context: SdkAvailabilityContext, input: { providerId?: string; serviceId?: string | null; locationId?: string; startsAt: string; endsAt: string; kind?: "blocked" | "open"; reason?: string }): Promise<AvailabilityException> {
  return context.request<AvailabilityException>("/api/v1/availability/exceptions", { method: "POST", body: JSON.stringify(input) });
}

export function deleteAvailabilityException(context: SdkAvailabilityContext, exceptionId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/availability/exceptions/${encodeURIComponent(exceptionId)}`, { method: "DELETE" });
}
